import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Typography, Box, Card, CardContent, Chip, Button,
  CircularProgress, Alert, Divider, Avatar, Collapse, IconButton,
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AccessTimeIcon     from '@mui/icons-material/AccessTime';
import LocalShippingIcon  from '@mui/icons-material/LocalShipping';
import StorefrontIcon     from '@mui/icons-material/Storefront';
import MyLocationIcon     from '@mui/icons-material/MyLocation';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import TwoWheelerIcon     from '@mui/icons-material/TwoWheeler';
import HomeIcon           from '@mui/icons-material/Home';
import ExpandMoreIcon     from '@mui/icons-material/ExpandMore';
import ExpandLessIcon     from '@mui/icons-material/ExpandLess';
import PersonIcon         from '@mui/icons-material/Person';
import PlaceIcon          from '@mui/icons-material/Place';
import CreditCardIcon     from '@mui/icons-material/CreditCard';
import HelpOutlineIcon    from '@mui/icons-material/HelpOutline';
import LogoutIcon         from '@mui/icons-material/Logout';
import restaurantService  from '../../services/restaurant.service';
import realtimeService    from '../../services/realtime.service';
import { useAuth }        from '../../context/AuthContext';
import { TZ }             from '../../utils/date';

const C = {
  purple: '#552583',
  gold:   '#FDB927',
  text:   '#1a1a1a',
  muted:  '#7a7a7a',
  light:  '#4a4a4a',
  border: '#e8e8e8',
  bg:     '#f5f5f5',
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DELIVERY_STEPS = [
  { key: 'pending',    label: 'En attente',       icon: <AccessTimeIcon sx={{ fontSize: 13 }} /> },
  { key: 'confirmed',  label: 'Confirmée',         icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  { key: 'preparing',  label: 'En préparation',    icon: <RestaurantMenuIcon sx={{ fontSize: 13 }} /> },
  { key: 'ready',      label: 'Prête',             icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  { key: 'picked_up',  label: 'Récupérée',         icon: <TwoWheelerIcon sx={{ fontSize: 13 }} /> },
  { key: 'delivering', label: 'En livraison',      icon: <LocalShippingIcon sx={{ fontSize: 13 }} /> },
  { key: 'delivered',  label: 'Livrée',            icon: <HomeIcon sx={{ fontSize: 13 }} /> },
];

const CAC_STEPS = [
  { key: 'pending',    label: 'En attente',        icon: <AccessTimeIcon sx={{ fontSize: 13 }} /> },
  { key: 'confirmed',  label: 'Confirmée',          icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  { key: 'preparing',  label: 'En préparation',     icon: <RestaurantMenuIcon sx={{ fontSize: 13 }} /> },
  { key: 'ready',      label: 'Prête à récupérer', icon: <StorefrontIcon sx={{ fontSize: 13 }} /> },
  { key: 'collected',  label: 'Récupérée',          icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
];

const ACTIVE_STATUSES = new Set(['pending', 'confirmed', 'preparing', 'ready', 'picked_up', 'delivering']);
const DONE_STATUSES   = new Set(['delivered', 'collected', 'cancelled']);

const getStatusLabel = (order) => {
  const isCAC = order.order_type === 'click_and_collect';
  return ({
    pending:    'En attente',
    confirmed:  'Confirmée',
    preparing:  'En préparation',
    ready:      isCAC ? 'Prête à récupérer' : 'Prête',
    picked_up:  'Récupérée par le livreur',
    delivering: 'En livraison',
    delivered:  'Livrée',
    collected:  'Récupérée',
    cancelled:  'Annulée',
  })[order.order_status] || order.order_status;
};

const STATUS_CHIP_COLOR = {
  pending: 'warning', confirmed: 'info', preparing: 'info', ready: 'success',
  picked_up: 'primary', delivering: 'primary', delivered: 'success',
  collected: 'success', cancelled: 'error',
};

// ── Sidebar ───────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: 'Commandes',           icon: <RestaurantMenuIcon />, to: '/restaurant/orders' },
  { label: 'Adresses',            icon: <PlaceIcon />,          to: '/profile/addresses' },
  { label: 'Moyens de paiement',  icon: <CreditCardIcon />,     to: '/profile/payment-methods' },
  { label: 'Aide',                icon: <HelpOutlineIcon />,    to: '/aide' },
];

const AccountSidebar = ({ user, onLogout }) => {
  const { pathname } = useLocation();
  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase()
    : '?';

  return (
    <Box sx={{
      width: { sm: 240, md: 260 }, flexShrink: 0,
      borderRight: `1px solid ${C.border}`,
      bgcolor: '#fff',
      minHeight: 'calc(100vh - 64px)',
      pt: 4, pb: 4,
      display: { xs: 'none', sm: 'flex' },
      flexDirection: 'column',
    }}>
      {/* Profil */}
      <Box sx={{ px: 3, mb: 3 }}>
        <Avatar sx={{
          width: 52, height: 52, mb: 1.5,
          bgcolor: C.purple, fontSize: '1.1rem', fontWeight: 700,
        }}>
          {initials}
        </Avatar>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {user?.first_name} {user?.last_name}
        </Typography>
        <Typography
          component={Link} to="/profile"
          variant="body2"
          sx={{ color: C.purple, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Gérer le compte
        </Typography>
      </Box>

      <Divider sx={{ borderColor: C.border, mb: 1 }} />

      {/* Nav items */}
      <Box sx={{ flex: 1, px: 1.5 }}>
        {NAV_ITEMS.map(item => {
          const active = pathname === item.to;
          return (
            <Box
              key={item.to}
              component={Link}
              to={item.to}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.25, borderRadius: '10px', mb: 0.25,
                textDecoration: 'none', cursor: 'pointer',
                bgcolor: active ? `${C.purple}12` : 'transparent',
                color: active ? C.purple : C.text,
                fontWeight: active ? 700 : 500,
                transition: 'background 0.15s',
                '&:hover': { bgcolor: active ? `${C.purple}18` : 'rgba(0,0,0,0.04)' },
              }}
            >
              <Box sx={{ color: active ? C.purple : C.muted, display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </Box>
              <Typography variant="body2" fontWeight={active ? 700 : 500} sx={{ color: 'inherit' }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: C.border, my: 1 }} />

      {/* Déconnexion */}
      <Box sx={{ px: 1.5 }}>
        <Box
          onClick={onLogout}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1.25, borderRadius: '10px',
            cursor: 'pointer', color: C.text,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <LogoutIcon sx={{ color: C.muted, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500}>Déconnexion</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Timeline inline ───────────────────────────────────────────────────────────
const StatusTimeline = ({ order }) => {
  const isCAC = order.order_type === 'click_and_collect';
  const steps = isCAC ? CAC_STEPS : DELIVERY_STEPS;
  const cur   = steps.findIndex(s => s.key === order.order_status);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0, mt: 2, mb: 1, overflowX: 'auto', pb: 0.5 }}>
      {steps.map((step, i) => {
        const done   = i < cur;
        const active = i === cur;
        return (
          <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (done || active) ? C.purple : 'rgba(0,0,0,0.06)',
                border: active ? `3px solid ${C.purple}` : 'none',
                boxShadow: active ? `0 0 12px ${C.purple}55` : 'none',
                color: (done || active) ? 'white' : 'rgba(0,0,0,0.3)',
                transition: 'all 0.4s',
                animation: active ? 'stepPulse 2s ease-in-out infinite' : 'none',
                '@keyframes stepPulse': {
                  '0%, 100%': { boxShadow: `0 0 6px ${C.purple}44` },
                  '50%': { boxShadow: `0 0 16px ${C.purple}77` },
                },
              }}>
                {done ? <CheckCircleIcon sx={{ fontSize: 13 }} /> : step.icon}
              </Box>
              <Typography variant="caption" sx={{
                fontSize: '0.6rem', fontWeight: active ? 700 : 400,
                color: active ? C.purple : done ? C.light : C.muted,
                whiteSpace: 'nowrap', maxWidth: 58, textAlign: 'center', lineHeight: 1.2,
              }}>
                {step.label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{
                height: 2, width: { xs: 16, sm: 28 }, mx: 0.25, mt: '-16px',
                bgcolor: done ? C.purple : 'rgba(0,0,0,0.1)',
                transition: 'background-color 0.4s', flexShrink: 0,
              }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Carte commande active ─────────────────────────────────────────────────────
const ActiveOrderCard = ({ order }) => {
  const isCAC        = order.order_type === 'click_and_collect';
  const isDelivering = ['picked_up', 'delivering'].includes(order.order_status);
  const isReady      = order.order_status === 'ready';
  const showMap      = !isCAC && ['confirmed', 'preparing', 'ready', 'picked_up', 'delivering'].includes(order.order_status);

  return (
    <Card sx={{
      mb: 2,
      border: `2px solid ${C.purple}33`,
      background: `linear-gradient(135deg, rgba(85,37,131,0.025), rgba(85,37,131,0.06))`,
      boxShadow: `0 4px 24px ${C.purple}14`,
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0,
                animation: 'livePulse 1.4s ease-in-out infinite',
                '@keyframes livePulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.5)' },
                  '50%': { boxShadow: '0 0 0 5px rgba(34,197,94,0)' },
                },
              }} />
              <Typography variant="body1" fontWeight={700}>{order.restaurant_name || 'Restaurant'}</Typography>
              {isCAC && (
                <Chip icon={<StorefrontIcon sx={{ fontSize: '11px !important' }} />}
                  label="À emporter" size="small"
                  sx={{ bgcolor: `${C.purple}14`, color: C.purple, border: `1px solid ${C.purple}28`, height: 18, fontSize: '0.6rem' }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">{order.order_number}</Typography>
          </Box>
          <Chip label={getStatusLabel(order)} color={STATUS_CHIP_COLOR[order.order_status] || 'default'} size="small" />
        </Box>

        <StatusTimeline order={order} />

        {/* Code retrait CAC */}
        {isCAC && isReady && order.delivery_code && (
          <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, textAlign: 'center', background: `linear-gradient(135deg, ${C.purple}10, ${C.purple}20)`, border: `1px solid ${C.purple}30` }}>
            <Typography variant="caption" sx={{ color: C.muted, display: 'block', mb: 0.5 }}>
              Code de retrait — montrez ce code au restaurant
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: C.purple, letterSpacing: '0.3em', fontFamily: 'monospace' }}>
              {order.delivery_code}
            </Typography>
          </Box>
        )}

        {/* ETA */}
        {!isCAC && order.estimated_delivery_time && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <AccessTimeIcon sx={{ fontSize: 15, color: C.muted }} />
            <Typography variant="body2" sx={{ color: C.light }}>
              Arrivée estimée :{' '}
              <strong>{new Date(order.estimated_delivery_time).toLocaleTimeString('fr-FR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}</strong>
            </Typography>
          </Box>
        )}

        {showMap && (
          <Button component={Link} to={`/restaurant/orders/${order.id}/tracking`}
            variant="contained" fullWidth startIcon={<MyLocationIcon />}
            sx={{
              mt: 2, py: 1.3, borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.88rem',
              background: isDelivering ? `linear-gradient(135deg, ${C.purple}, #7B3CB5)` : `linear-gradient(135deg, #2D4A35, #1E3525)`,
              boxShadow: 'none', '&:hover': { opacity: 0.9, boxShadow: 'none' },
            }}
          >
            {isDelivering ? 'Suivre le livreur en direct' : 'Voir sur la carte'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ── Carte commande passée (compact, expansible) ───────────────────────────────
const PastOrderCard = ({ order }) => {
  const [open, setOpen] = useState(false);
  const isCAC = order.order_type === 'click_and_collect';

  return (
    <Card sx={{ mb: 1.5, border: `1px solid ${C.border}`, boxShadow: 'none' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box onClick={() => setOpen(o => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>{order.restaurant_name || 'Restaurant'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(order.created_at).toLocaleDateString('fr-FR', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              {' · '}{order.order_number}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: C.purple }}>{fmt(order.total_amount)} €</Typography>
            <Chip label={getStatusLabel(order)} color={STATUS_CHIP_COLOR[order.order_status] || 'default'} size="small" />
            <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
        </Box>

        <Collapse in={open}>
          <Divider sx={{ borderColor: C.border }} />
          <Box sx={{ p: 2, pt: 1.5 }}>
            {order.items?.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="body2">{item.quantity}x {item.name || item.menu_item_name}</Typography>
                <Typography variant="body2" fontWeight={500}>{fmt(item.total_price || item.quantity * item.unit_price)} €</Typography>
              </Box>
            ))}
            <Divider sx={{ borderColor: C.border, my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">Sous-total</Typography>
              <Typography variant="caption">{fmt(order.subtotal)} €</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">{isCAC ? 'Retrait' : 'Livraison'}</Typography>
              <Typography variant="caption">{order.delivery_fee === 0 || order.delivery_fee === '0.00' ? 'Gratuit' : `${fmt(order.delivery_fee)} €`}</Typography>
            </Box>
            {order.service_fee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Frais de service</Typography>
                <Typography variant="caption">{fmt(order.service_fee)} €</Typography>
              </Box>
            )}
            <Divider sx={{ borderColor: C.border, my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={700}>Total</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: C.purple }}>{fmt(order.total_amount)} €</Typography>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────
const RestaurantOrders = () => {
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [liveCount, setLiveCount] = useState(0);
  const pollRef   = useRef(null);
  const joinedRef = useRef(new Set());

  const loadOrders = useCallback(async () => {
    try {
      const data = await restaurantService.getMyOrders();
      setOrders(data.orders || []);
    } catch (err) {
      setError('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, []);

  const patchOrderStatus = useCallback((orderId, newStatus) => {
    setOrders(prev => prev.map(o =>
      String(o.id) === String(orderId) ? { ...o, order_status: newStatus } : o
    ));
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  useEffect(() => {
    const active = orders.filter(o => ACTIVE_STATUSES.has(o.order_status));
    setLiveCount(active.length);
    active.forEach(o => {
      if (!joinedRef.current.has(o.id)) {
        realtimeService.joinOrder(o.id);
        joinedRef.current.add(o.id);
      }
    });
    realtimeService.onStatusChange((data) => patchOrderStatus(data.orderId, data.status));
    if (active.length > 0) {
      pollRef.current = setInterval(loadOrders, 30_000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [orders, loadOrders, patchOrderStatus]);

  useEffect(() => {
    return () => {
      joinedRef.current.forEach(id => realtimeService.leaveOrder(id));
      realtimeService.off('order-status-changed');
    };
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const activeOrders = orders.filter(o => ACTIVE_STATUSES.has(o.order_status));
  const pastOrders   = orders.filter(o => DONE_STATUSES.has(o.order_status));

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)', bgcolor: C.bg }}>
      {/* Sidebar */}
      <AccountSidebar user={user} onLogout={handleLogout} />

      {/* Contenu principal */}
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 4 }, py: 4 }}>
        {/* Titre */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800}>Commandes</Typography>
          {liveCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e',
                animation: 'livePulse 1.4s ease-in-out infinite',
                '@keyframes livePulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.5)' },
                  '50%': { boxShadow: '0 0 0 4px rgba(34,197,94,0)' },
                },
              }} />
              <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                {liveCount} commande{liveCount > 1 ? 's' : ''} en cours · Suivi en direct
              </Typography>
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress sx={{ color: C.purple }} />
          </Box>
        ) : orders.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6, boxShadow: 'none', border: `1px solid ${C.border}` }}>
            <CardContent>
              <RestaurantMenuIcon sx={{ fontSize: 64, color: 'rgba(0,0,0,0.12)', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>Aucune commande</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Vous n'avez pas encore passé de commande restaurant.
              </Typography>
              <Button component={Link} to="/restaurants" variant="contained"
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: C.purple }}>
                Découvrir les restaurants
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {activeOrders.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="overline" sx={{ color: C.purple, fontWeight: 700, letterSpacing: 2, fontSize: '0.7rem' }}>
                  EN COURS
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {activeOrders.map(o => <ActiveOrderCard key={o.id} order={o} />)}
                </Box>
              </Box>
            )}

            {pastOrders.length > 0 && (
              <Box>
                <Typography variant="overline" sx={{ color: C.muted, fontWeight: 700, letterSpacing: 2, fontSize: '0.7rem' }}>
                  HISTORIQUE
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {pastOrders.map(o => <PastOrderCard key={o.id} order={o} />)}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default RestaurantOrders;
